import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const eras = [
  {
    year: "1980",
    title: "The Mainframe Era",
    body: "JCL and COBOL at Liberty Mutual. Batch windows, green screens, and systems engineered to run for decades — many still do.",
    tags: ["JCL", "COBOL", "IMS", "CICS", "DB2", "VSAM"],
    image: "https://images.unsplash.com/photo-1766941234616-ba978cb000e3?fm=jpg&q=85&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  },
  {
    year: "1990s",
    title: "Client / Server",
    body: "The desktop awakens. C++ and Visual Basic bring computing to the user, and business logic spreads across a new frontier.",
    tags: ["C++", "Visual Basic", "Client/Server"],
  },
  {
    year: "2000s",
    title: "The Web",
    body: "Applications escape the desktop entirely. Browsers become the interface; connectivity becomes the assumption.",
    tags: ["Web Applications", "Portals", "Services"],
  },
  {
    year: "2010s",
    title: "Enterprise Platforms",
    body: "Salesforce proves that platforms — not just programs — run the business. Configuration begins to rival code.",
    tags: ["Salesforce", "SaaS", "Platforms"],
  },
  {
    year: "2015+",
    title: "ServiceNow",
    body: "A decade as Solution Architect and Platform Developer. App Engine, workflows, workspaces, UI Builder, integrations — the enterprise operating system.",
    tags: ["App Engine", "Workflow", "UI Builder", "REST APIs", "Integrations"],
  },
  {
    year: "Now",
    title: "The AI Horizon",
    body: "AI Agents, Build Agent, and AI-assisted development collapse the distance between idea and application. The canvas is wider than ever.",
    tags: ["AI Agents", "Build Agent", "AI-Assisted Development"],
    image: "https://images.pexels.com/photos/17489163/pexels-photo-17489163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const Timeline = () => {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [range, setRange] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref });
  const x = useTransform(scrollYProgress, [0, 1], [0, -range]);

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      const overflow = trackRef.current.scrollWidth - window.innerWidth;
      setRange(Math.max(overflow + 48, 0));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <section ref={ref} data-testid="tech-timeline" className="relative h-[340vh] bg-ink">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="mx-auto w-full max-w-[1400px] px-6 md:px-12 mb-10">
          <p className="label-mono mb-4">Four decades of fluency</p>
          <h2 className="font-serif text-4xl md:text-6xl text-slate-100 tracking-tight">
            From COBOL to Claude
          </h2>
        </div>
        <motion.div ref={trackRef} style={{ x }} className="flex gap-8 md:gap-14 pl-6 md:pl-12 pr-6 md:pr-12">
          {eras.map((era) => (
            <article
              key={era.year}
              data-testid={`timeline-era-${era.year.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className="group relative shrink-0 w-[80vw] md:w-[520px] border hairline bg-ink-light p-8 md:p-10 transition-[border-color,transform] duration-500 hover:border-amber-600/50 hover:-translate-y-1"
            >
              {era.image && (
                <div className="relative mb-8 h-44 md:h-52 overflow-hidden">
                  <img
                    src={era.image}
                    alt={era.title}
                    className="h-full w-full object-cover heritage-image"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-amber-900/20 mix-blend-overlay" />
                </div>
              )}
              <p className="font-mono-tech text-5xl md:text-6xl text-amber-500/25 tracking-tight">
                {era.year}
              </p>
              <h3 className="font-serif text-3xl md:text-4xl text-slate-100 mt-4 tracking-tight">
                {era.title}
              </h3>
              <p className="mt-4 text-slate-400 font-light leading-relaxed">{era.body}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {era.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono-tech text-[10px] tracking-[0.15em] uppercase text-amber-400/80 border border-amber-800/40 px-3 py-1"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Timeline;
